import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  ArrowUpRight,
  Building2,
  Trees,
  Coins,
  Globe2,
  Users2,
  ShieldCheck,
  FileText,
  Scale,
  Compass,
  ClipboardList,
  FileSignature,
  Banknote,
  Rocket,
} from "lucide-react";
import HeaderV2 from "./HeaderV2";
import FooterV2 from "./FooterV2";
import V2PageHero from "./sections/V2PageHero";
import ServicesV2 from "./sections/ServicesV2";
import FAQV2 from "./sections/FAQV2";
import CTAV2 from "./sections/CTAV2";
import InlineCTA from "./sections/InlineCTA";
import useSmoothScroll from "../../hooks/useSmoothScroll";
import { useConsultation } from "../../context/ConsultationContext";

const DATA = {
  mainland: {
    eyebrow: "Company Formation · Mainland",
    title: "Trade the entire UAE —",
    highlight: "no boundaries.",
    copy: "A Mainland LLC or sole establishment gives you direct access to the UAE market, public sector contracts and unrestricted trade across all seven Emirates.",
    image:
      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=2400&q=80",
    includes: [
      "DED Trade License (commercial, professional, industrial)",
      "Memorandum of Association (MoA) drafting & notarisation",
      "Ejari office lease & approvals",
      "Initial approvals, tasheel & immigration file",
      "Corporate bank account introduction",
      "Annual compliance calendar & VAT registration",
    ],
    bestFor: ["Retail & F&B", "Construction & contracting", "Local professional services", "High-growth SMEs"],
    highlights: [
      { icon: Building2, t: "100% Ownership", d: "Full foreign ownership on most commercial activities." },
      { icon: Globe2, t: "Unlimited Market", d: "Trade anywhere in the UAE and bid on government tenders." },
      { icon: Users2, t: "Unlimited Visas", d: "Visa quota scales with your Ejari office size." },
      { icon: ShieldCheck, t: "Strong Reputation", d: "Preferred by larger banks and institutional buyers." },
    ],
    structures: [
      { name: "LLC", d: "Most flexible for multi-activity trading and employment." },
      { name: "Sole Establishment", d: "Single owner for professional or consultancy work." },
      { name: "Civil Company", d: "Professional partnerships (law, medicine, engineering)." },
      { name: "Branch Office", d: "UAE arm of a foreign parent — same legal identity." },
    ],
    timeline: "2–4 weeks",
    fromCost: "AED 18,500*",
  },
  freezone: {
    eyebrow: "Company Formation · Freezone",
    title: "0% tax. 100% ownership.",
    highlight: "Global by design.",
    copy: "Freezone structures are tailor-made for international founders — fast incorporation, full repatriation of capital and 0% corporate tax on qualifying income.",
    image:
      "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=2400&q=80",
    includes: [
      "Freezone license (IFZA, DMCC, Meydan, SHAMS, RAKEZ…)",
      "Flexi-desk, virtual or physical office",
      "Shareholder & UBO documentation",
      "Investor visa + dependents sponsorship",
      "Corporate bank account preparation",
      "ESR, UBO & VAT compliance setup",
    ],
    bestFor: ["Tech startups & SaaS", "Consultants & freelancers", "E-commerce & trading", "Holding & IP companies"],
    highlights: [
      { icon: Coins, t: "0% Corporate Tax", d: "On qualifying income under the Qualifying Freezone Person regime." },
      { icon: Trees, t: "45+ Freezones", d: "Activity and cost-matched to your business model." },
      { icon: Globe2, t: "100% Repatriation", d: "Full repatriation of capital and profits — no restrictions." },
      { icon: Building2, t: "Flexible Office", d: "Flexi-desk, virtual office or full physical space." },
    ],
    structures: [
      { name: "FZCO / FZ-LLC", d: "Standard freezone entity with multiple shareholders." },
      { name: "FZE", d: "Single-shareholder freezone entity — fastest to incorporate." },
      { name: "Freelance Permit", d: "Solo operator permit — low-cost entry point." },
      { name: "Branch of Foreign Co.", d: "UAE freezone branch of a foreign parent." },
    ],
    timeline: "1–2 weeks",
    fromCost: "AED 12,500*",
  },
};

const STEPS = [
  { icon: Compass, t: "Discovery Call", d: "Strategy session with a senior advisor." },
  { icon: ClipboardList, t: "Structure Quote", d: "Mainland vs Freezone, written transparent quote." },
  { icon: FileSignature, t: "Documentation", d: "Shareholder KYC, UBO, MoA & notarisation." },
  { icon: ShieldCheck, t: "License Issuance", d: "Trade name, approvals & official UAE license." },
  { icon: Banknote, t: "Bank & Tax", d: "Bank introductions, VAT & corporate tax setup." },
  { icon: Rocket, t: "Visa & Launch", d: "Investor visa, Emirates ID, ready to invoice." },
];

export default function CompanyFormationV2() {
  useSmoothScroll(true);
  const { type } = useParams();
  const { open } = useConsultation();
  const entry = DATA[type] || DATA.mainland;
  const otherType = type === "freezone" ? "mainland" : "freezone";

  return (
    <div data-testid={`v2-formation-${type}-page`} className="bg-white">
      <HeaderV2 />
      <main>
        <V2PageHero
          eyebrow={entry.eyebrow}
          title={entry.title}
          highlight={entry.highlight}
          copy={entry.copy}
          image={entry.image}
        />

        {/* Highlights row */}
        <section className="bg-white py-10 md:py-14">
          <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {entry.highlights.map(({ icon: Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="p-6 rounded-sm border border-slate-200 bg-white hover:border-[#3366FF]/40 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="mt-4 font-heading font-semibold text-[#0F172A]">{t}</div>
                <div className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</div>
              </motion.div>
            ))}
          </div>
        </section>

        <InlineCTA
          eyebrow="Talk to a senior advisor"
          title={`Get a ${type === "freezone" ? "Freezone" : "Mainland"} setup roadmap tailored to your activity.`}
          buttonLabel="Book a call"
          service={type}
        />

        {/* Includes + sticky pricing card */}
        <section className="bg-[#F8FAFC] py-10 md:py-14">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <p className="overline text-[#3366FF]">What's included</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight text-[#0F172A]">
                A fully <span className="font-black text-[#002366]">turnkey</span> setup —
                no bolt-ons, no surprises.
              </h2>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {entry.includes.map((t) => (
                  <li key={t} className="flex gap-3 text-slate-700">
                    <Check className="w-5 h-5 shrink-0 text-[#3366FF] mt-0.5" />
                    <span className="text-sm md:text-base">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <p className="overline text-[#3366FF] mb-4">Available structures</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {entry.structures.map((s) => (
                    <div
                      key={s.name}
                      className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-sm hover:border-[#3366FF]/40 transition-colors"
                    >
                      <FileText className="w-4 h-4 text-[#3366FF] shrink-0 mt-0.5" />
                      <div>
                        <div className="font-heading font-semibold text-[#0F172A] text-sm">
                          {s.name}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-5">
              <div className="rounded-sm border border-slate-200 p-7 md:p-8 bg-white shadow-sm lg:sticky lg:top-28">
                <p className="overline text-[#3366FF]">Best for</p>
                <ul className="mt-5 space-y-3">
                  {entry.bestFor.map((b) => (
                    <li key={b} className="text-slate-800 flex items-center gap-3 py-1">
                      <Scale className="w-4 h-4 text-[#3366FF]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 p-5 rounded-sm bg-[#002366] text-white">
                  <div className="text-sm text-slate-300">Est. time to license</div>
                  <div className="mt-1 font-heading font-black text-2xl">
                    {entry.timeline}
                  </div>
                  <div className="mt-3 text-sm text-slate-300">Est. year-one cost</div>
                  <div className="mt-1 font-heading font-black text-2xl text-[#6B8AFF]">
                    from {entry.fromCost}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">
                    *Indicative. Final figure depends on activity, visa count and office.
                  </p>
                </div>
                <button
                  onClick={() => open(type || "other")}
                  className="btn-gold mt-6 w-full"
                >
                  Request a tailored quote <ArrowUpRight className="w-4 h-4" />
                </button>
                <Link
                  to={`/company-formation/${otherType}`}
                  className="mt-3 block text-center text-sm text-[#002366] underline-offset-4 hover:underline"
                >
                  Compare with {otherType === "mainland" ? "Mainland" : "Freezone"} →
                </Link>
              </div>
            </aside>
          </div>
        </section>

        {/* Process timeline (compact) */}
        <section className="bg-white py-10 md:py-14">
          <div className="container-x">
            <div className="max-w-2xl mb-7">
              <p className="overline text-[#3366FF]">Your 6-step path</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
                From first call to{" "}
                <span className="font-black text-[#002366]">first invoice</span>.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {STEPS.map(({ icon: Icon, t, d }, i) => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                  className="relative p-6 rounded-sm border border-slate-200 bg-white hover:shadow-md hover:border-[#3366FF]/40 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="font-heading font-black text-3xl text-slate-200">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-[#0F172A]">{t}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <ServicesV2 />
        <FAQV2 />
        <CTAV2 />
      </main>
      <FooterV2 />
    </div>
  );
}
